import type { UploadMethod } from "@/analytics/surfaces";
import { orgAPI } from "@/api/api";
import { fileRecordKeys, workspaceTreeQuery } from "@/api/fileRecords";
import {
  generateUniqueName,
  useFileConflictResolver,
} from "@/components/FileConflictResolver";
import { exceedsFileRecordMaxSize } from "@/constants/file-limits";
import { useFileRecordUploadQueue } from "@/hooks/useFileRecordUploadQueue";
import type { FolderUploadTree } from "@/utils/traverseDataTransferItems";
import { withUploadSlot } from "@/utils/uploadConcurrency";
import type { FileRecord } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef } from "react";
import toast from "react-hot-toast";
import { getDirectChildren } from "../utils/folderPaths";

// A "doc" is just an empty `.md` file the collaborative editor takes over on
// first open — mirrors the canvas "New Doc" (see `useCreateDocOnCanvas`).
const DOC_DEFAULT_NAME = "New Doc";
const DOC_CONTENT_TYPE = "text/markdown";

/**
 * Workspace-tile shape consumed by `WorkspaceDetailPage`'s
 * `pendingEntries` projection. The queue primitive carries a richer set
 * of statuses (initializing / pending / processing / processed / failed)
 * — workspace projects them down to the binary `uploading | error`
 * pair the ghost-tile UI was built around.
 */
export interface UploadQueueItem {
  tempId: string;
  file: File;
  name: string;
  status: "uploading" | "error";
  kind: "create" | "replace";
  fileRecordId?: string;
  error?: string;
  /**
   * Destination parent directory (`""` = workspace root, `"foo/"` =
   * subfolder). Page-level ghost rendering filters by this so a folder
   * drop only renders ghost tiles in the folder the file is actually
   * destined for, instead of flashing them at the user's current view
   * before the real record settles into the right path.
   */
  destinationParentPath?: string;
}

/**
 * Workspace-side upload adapter. Thin wrapper around
 * `useFileRecordUploadQueue` that:
 *
 * - Routes the dropped files through the conflict resolver before
 *   enqueueing (Replace contents / Keep both / Skip / Cancel dialog).
 * - Filters out items the queue has advanced to `processing | processed`
 *   so the ghost tile yields to the real `FileRecordCard` as soon as the
 *   S3 webhook flips the row.
 * - Projects queue state to the workspace tile's `uploading | error`
 *   shape and exposes `suppressedRecordIds` so the page doesn't render
 *   a "pending" duplicate next to the ghost.
 *
 * Public hook surface unchanged.
 */
export function useWorkspaceUploadQueue(workspaceId: string | undefined) {
  const { resolveUploads, resolveFolderUpload } = useFileConflictResolver();
  const queue = useFileRecordUploadQueue();
  const queryClient = useQueryClient();

  const enqueueFiles = useCallback(
    (files: File[], currentPath: string, method: UploadMethod = "drop") => {
      if (!workspaceId) return;
      if (files.length === 0) return;

      // The queue applies the size cap itself, so the filtering step
      // that used to live here is gone. Conflict resolution still runs
      // in the workspace adapter because `resolveUploads` is async (it
      // may surface a dialog) and we want it to settle before the queue
      // adds initializing tiles.
      void (async () => {
        const resolved = await resolveUploads({
          uploads: files.map((file) => ({
            file,
            destinationParentPath: currentPath,
          })),
          scope: { class: "workspace", workspace_id: workspaceId },
        });
        if (resolved === null) return;

        await queue.enqueue(
          resolved.map((op) => ({
            file: op.file,
            resolvedPath: op.path,
            displayName: op.name,
            contentType: op.contentType,
            kind: op.kind,
          })),
          {
            class: "workspace",
            workspace_id: workspaceId,
            parentPath: currentPath,
          },
          { method },
        );
      })();
    },
    [workspaceId, resolveUploads, queue],
  );

  /**
   * Drop of one or more OS folders. Walks the resolver to reconcile
   * folder + file collisions, materialises the directory markers
   * (`is_folder: true` records) shallow-to-deep, then hands the
   * resolved file ops to the existing queue with each file's full
   * destination path baked in.
   *
   * Folder marker creates patch the workspace list cache directly so
   * the dropped tree appears in the grid immediately, without waiting
   * for the SSE round-trip. File uploads patch through the queue +
   * `performFileRecordUpload` (which now mirrors workspace cache the
   * way it already mirrors canvas).
   *
   * `tree.looseFiles` are merged into the resolver as files at the
   * current path so a mixed drop (folder + top-level file) is handled
   * in a single conflict pass.
   */
  const enqueueTree = useCallback(
    (
      tree: FolderUploadTree,
      currentPath: string,
      method: UploadMethod = "tree_drop",
    ) => {
      if (!workspaceId) return;
      if (
        tree.folders.length === 0 &&
        tree.files.length === 0 &&
        tree.looseFiles.length === 0
      ) {
        return;
      }

      const mergedFiles: { file: File; relativePath: string }[] = [
        ...tree.files,
        ...tree.looseFiles.map((file) => ({ file, relativePath: file.name })),
      ];

      void (async () => {
        const resolved = await resolveFolderUpload({
          tree: { folders: tree.folders, files: mergedFiles },
          parentPath: currentPath,
          workspaceId,
        });
        if (resolved === null) return;

        const workspaceKey = fileRecordKeys.workspace(workspaceId);
        const patchWorkspaceList = (record: FileRecord) => {
          queryClient.setQueryData(fileRecordKeys.detail(record.id), record);
          queryClient.setQueryData<FileRecord[]>(workspaceKey, (prev) => {
            if (!prev) return prev;
            const idx = prev.findIndex((r) => r.id === record.id);
            if (idx === -1) return [...prev, record];
            const next = prev.slice();
            next[idx] = { ...prev[idx], ...record };
            return next;
          });
        };

        // Folder markers are cheap (no S3 POST) but they share the
        // same `POST /file-records` endpoint as file uploads, so we
        // route them through the same global slot pool. With the
        // pool capped at 10 in-flight, a 50-folder drop fans out at
        // a sustainable rate instead of all at once. Order doesn't
        // strictly matter today (the server doesn't enforce parent
        // existence), but the FIFO pool emits them shallow-to-deep
        // anyway since `folderOps` is already in that order.
        const folderResults = await Promise.allSettled(
          resolved.folderOps.map((op) =>
            withUploadSlot(() =>
              orgAPI.fileRecords
                .create({
                  provider: "s3",
                  destination: {
                    class: "workspace",
                    workspace_id: workspaceId,
                    path: op.path,
                    is_folder: true,
                  },
                })
                .then((record) => {
                  patchWorkspaceList(record);
                  return record;
                }),
            ),
          ),
        );
        const folderFailures = folderResults.filter(
          (r) => r.status === "rejected",
        ).length;
        if (folderFailures > 0) {
          toast.error(
            folderFailures === 1
              ? "Failed to create 1 folder"
              : `Failed to create ${folderFailures} folders`,
          );
        }

        if (resolved.fileOps.length === 0) return;

        const uploadFiles = resolved.fileOps.map((op) => ({
          file: op.file,
          resolvedPath: op.path,
          displayName: op.name,
          contentType: op.contentType,
          kind: op.kind,
        }));
        const uploadTarget = {
          class: "workspace" as const,
          workspace_id: workspaceId,
          parentPath: currentPath,
        };

        // Oversized files are dropped inside `queue.enqueue`, which toasts
        // each one itself and never runs them through the callbacks below.
        // Leaving them out of the count keeps the totals honest instead of
        // promising uploads that were never attempted.
        const expected = uploadFiles.filter(
          (f) => !exceedsFileRecordMaxSize(f.file),
        ).length;
        if (expected === 0) {
          await queue.enqueue(uploadFiles, uploadTarget, { method });
          return;
        }

        // A loading toast, not a fire-and-forget one: the upload can run
        // for minutes and the user's complaint is that nothing shows while
        // it does. Resolved in place below on every exit path.
        const toastId = toast.loading(
          expected === 1 ? "Uploading 1 file…" : `Uploading ${expected} files…`,
        );

        let uploaded = 0;
        let failed = 0;
        try {
          await queue.enqueue(uploadFiles, uploadTarget, {
            method,
            onUploaded: () => {
              uploaded += 1;
            },
            onUploadError: () => {
              failed += 1;
            },
          });
        } catch {
          // `enqueue` fans out per-file handlers that catch their own
          // errors, so reaching here means the whole batch died before any
          // of them ran.
          toast.error("Upload failed", { id: toastId });
          return;
        }

        // Neither callback fired for any file: every one was cancelled
        // (`useFileRecordUploadQueue` returns before both `onUploaded` and
        // `onUploadError` on its cancellation paths). Nothing completed, so
        // there is nothing to report — just clear the loading toast.
        if (uploaded === 0 && failed === 0) {
          toast.dismiss(toastId);
        } else if (failed === 0) {
          toast.success(
            uploaded === 1 ? "Uploaded 1 file" : `Uploaded ${uploaded} files`,
            { id: toastId },
          );
        } else if (uploaded === 0) {
          toast.error(
            failed === 1
              ? "Failed to upload 1 file"
              : `Failed to upload ${failed} files`,
            { id: toastId },
          );
        } else {
          toast.error(`Uploaded ${uploaded} of ${uploaded + failed} files`, {
            id: toastId,
          });
        }
      })();
    },
    [workspaceId, resolveFolderUpload, queue, queryClient],
  );

  /**
   * Create a brand-new, empty markdown doc in `currentPath` and enqueue it
   * through this same queue instance (so it gets the normal ghost-tile /
   * suppression treatment, unlike a one-off standalone queue).
   *
   * We resolve the unique leaf name ourselves against the *direct children of
   * the current folder* and hand the queue a full `resolvedPath`. Letting the
   * raw queue resolve it would scan leaf names across the whole workspace and
   * rename "New Doc.md" in an empty folder just because another folder already
   * has one. The collision set is the folder's cached records (what the grid
   * shows) plus any uploads still in flight to the same folder — the latter
   * covers a rapid second click (or a concurrent same-name upload) whose
   * record hasn't been acknowledged by the server yet, without leaving a
   * reservation behind if the upload is later dismissed. Going through
   * `enqueue` directly (not `enqueueFiles`) also skips the conflict resolver
   * dialog — a one-click "New document" should silently land as
   * "New Doc (1).md", matching the canvas.
   *
   * `onCreated` fires once `POST /file-records` returns, so the caller can
   * open the editor straight away.
   */
  // Doc names claimed by createDoc calls that are still in flight, keyed by
  // parent path. Lives in a ref so a resumed async call sees claims made
  // after its own render was captured.
  const reservedDocNames = useRef(new Map<string, Set<string>>());

  const createDoc = useCallback(
    async (
      currentPath: string,
      options?: { onCreated?: (record: FileRecord) => void },
    ) => {
      if (!workspaceId) return;

      // fetchQuery, not getQueryData: with the workspace list no longer
      // pre-warmed, "New document" can run against a cold cache, and an empty
      // collision set would skip dedupe. staleTime Infinity makes any cached
      // entry resolve without a network hop (the old synchronous behavior);
      // retry false caps the cold-cache failure path at one attempt so a dead
      // list endpoint can't stall creation behind retries.
      let records: FileRecord[];
      try {
        records = await queryClient.fetchQuery({
          ...workspaceTreeQuery(workspaceId),
          staleTime: Infinity,
          retry: false,
        });
      } catch {
        // List fetch failed (offline / 5xx): fall back to whatever the cache
        // holds — possibly stale, but the server 409s duplicate paths rather
        // than suffixing them, so stale collision data beats none.
        records =
          queryClient.getQueryData<FileRecord[]>(
            fileRecordKeys.workspace(workspaceId),
          ) ?? [];
      }
      const taken = new Set<string>([
        // Names reserved by createDoc calls still inside their await window.
        // The fetch above suspends between name computation and the queue
        // re-render, so a second rapid activation can resume against a stale
        // records/queue snapshot; the ref is read at continuation time, so it
        // always sees an in-flight sibling's claim.
        ...(reservedDocNames.current.get(currentPath) ?? []),
        ...getDirectChildren(records, currentPath)
          // Folder markers (trailing "/") share no namespace with files, and
          // deleted rows have freed their name — mirror the leaf-name set the
          // queue builds for ordinary uploads.
          .filter((r) => r.status !== "deleted" && !r.path.endsWith("/"))
          .map((r) => r.path.split("/").pop())
          .filter((leaf): leaf is string => !!leaf),
        // Uploads to this folder whose POST /file-records hasn't returned yet
        // ("initializing"). Once it returns, performFileRecordUpload has
        // already patched the list cache, so the record above is authoritative
        // for every later status — counting those too would keep a name
        // blocked after the doc is deleted, since processed items linger in
        // the queue. The item lands here synchronously on enqueue and is
        // dropped on cancel, so there's nothing to leak.
        ...queue.queue
          .filter(
            (q) =>
              q.status === "initializing" &&
              q.destinationParentPath === currentPath,
          )
          .map((q) => q.name),
      ]);
      const uniqueName = generateUniqueName(
        `${DOC_DEFAULT_NAME}.md`,
        false,
        taken,
      );
      let reservations = reservedDocNames.current.get(currentPath);
      if (!reservations) {
        reservations = new Set();
        reservedDocNames.current.set(currentPath, reservations);
      }
      reservations.add(uniqueName);
      const releaseReservation = () => reservations.delete(uniqueName);

      // Zero bytes is a valid body — the S3 presign policy's
      // content-length-range starts at 0, and the collaborative editor treats
      // "no seed content" as an empty document.
      const file = new File([], uniqueName, { type: DOC_CONTENT_TYPE });

      void queue.enqueue(
        [
          {
            file,
            resolvedPath: `${currentPath}${uniqueName}`,
            displayName: uniqueName,
            contentType: DOC_CONTENT_TYPE,
            kind: "create",
          },
        ],
        { class: "workspace", workspace_id: workspaceId },
        {
          method: "new_doc",
          onRecordCreated: (record) => {
            // The list cache is patched by now, so the record itself blocks
            // the name from here on.
            releaseReservation();
            options?.onCreated?.(record);
          },
          onUploadError: () => {
            releaseReservation();
            toast.error("Failed to create doc");
          },
        },
      );
    },
    [workspaceId, queue, queryClient],
  );

  const cancelUpload = useCallback(
    (tempId: string) => {
      queue.removeItem(tempId);
    },
    [queue],
  );

  /**
   * Project queue state onto the workspace tile shape, filtering out
   * items the per-id sub has already advanced to `processing | processed`
   * (the real card has rendered for those — see ghost-dismissal below).
   */
  const projectedQueue = useMemo<UploadQueueItem[]>(
    () =>
      queue.queue
        .filter((q) => q.status !== "processing" && q.status !== "processed")
        .map((q) => ({
          tempId: q.tempId,
          file: q.file,
          name: q.name,
          status: q.status === "failed" ? "error" : "uploading",
          kind: q.kind,
          fileRecordId: q.fileRecordId,
          error: q.error,
          destinationParentPath: q.destinationParentPath,
        })),
    [queue.queue],
  );

  /**
   * Ids of FileRecords currently represented by a ghost tile so the page
   * can hide the matching pending record from the unified list — a
   * single upload should never render twice.
   */
  const suppressedRecordIds = useMemo(
    () =>
      new Set(
        queue.queue
          .filter((q) => q.status !== "processing" && q.status !== "processed")
          .map((q) => q.fileRecordId)
          .filter((id): id is string => !!id),
      ),
    [queue.queue],
  );

  return {
    queue: projectedQueue,
    enqueueFiles,
    enqueueTree,
    createDoc,
    cancelUpload,
    suppressedRecordIds,
  };
}
